import { Fragment, isValidElement, useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Keys } from '../../utils/keys';
import { Icon, type IconName } from '../../icons/Icon';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { Kbd } from '../Kbd/Kbd';
import { Spinner } from '../Spinner/Spinner';
import { ModalOverlay } from '../Dialog/ModalOverlay';
import { groupCommands, matchCommand, rankCommands, type CommandGroup, type MatchRange, type RankedCommand } from './matching';
import { getRecent, getRecentSnapshots, pushRecent, pushRecentSnapshot } from './recent';
import { DEFAULT_COMMAND_GROUP_ORDER, defaultCommandPaletteMessages, parseScopedQuery, type Command, type CommandPaletteMessages, type CommandSource } from './types';
import { useDsT } from '../../services/i18n';

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commands: Command[];
  /**
   * Server-side search merged with local results (debounced). Kept for simple hosts; prefer `sources`, which
   * get an `AbortSignal` and report loading/failure per source.
   */
  loadCommands?: (query: string, signal?: AbortSignal) => Promise<Command[]>;
  /** Result providers queried in parallel as the user types (see {@link CommandSource}). */
  sources?: CommandSource[];
  /** Replays a remembered navigation command (a record found by a source) from "Recent". */
  onNavigate?: (href: string) => void;
  placeholder?: string;
  emptyMessage?: string;
  /** Localized strings; anything omitted falls back to English. */
  messages?: Partial<CommandPaletteMessages>;
  /** Persist recent command ids under this localStorage key. */
  recentKey?: string;
  /** Number of recent commands shown when the query is empty. */
  maxRecent?: number;
  initialQuery?: string;
  /** Preferred order of group headings; unlisted groups keep their first-seen order after these. */
  groupOrder?: string[];
  /** The heading shown for a group id (`Actions` → the viewer's language); the id itself otherwise. */
  groupLabel?: (group: string) => string;
  /** Inline "no results — create it" affordance. Receives the trimmed query. */
  onCreate?: (query: string) => void;
  /** Label for the create row; defaults to `Create “<query>”`. */
  createLabel?: string | ((query: string) => ReactNode);
  /** Footer hint slot; defaults to keyboard hints. */
  footer?: ReactNode;
  /** Tab on a result "peeks" it (opens it in the detail drawer) without closing the palette. */
  onPeek?: (command: Command) => void;
  /** Renders a result's `status` code (a StatusBadge in the app); the palette itself never imports the registry. */
  renderStatus?: (command: Command) => ReactNode;
  className?: string;
}

const DEBOUNCE_MS = 150;
const LEGACY_SOURCE_ID = '__loadCommands';
/** Score given to a server result the local matcher cannot explain (the server matched fields we do not see). */
const REMOTE_BASE_SCORE = 40;

type SourceState = { status: 'loading' | 'done' | 'error'; items: Command[] };

/** Group ids the palette itself names; their headings follow the viewer's language unless `groupLabel` says otherwise. */
const BUILT_IN_GROUPS = new Set([...DEFAULT_COMMAND_GROUP_ORDER, 'Commands']);

const groupHeading =
  'sticky top-0 z-10 bg-surface-3 px-2 pb-1 pt-2 type-caption font-strong uppercase tracking-caps text-fg-subtle';

function renderIcon(icon: Command['icon']): ReactNode {
  if (typeof icon === 'string') return <Icon name={icon as IconName} size={14} />;
  return isValidElement(icon) ? icon : null;
}

/** Renders `text` with the matched ranges emphasised. */
export function Highlight({ text, ranges }: { text: string; ranges?: MatchRange[] }) {
  if (!ranges?.length) return <>{text}</>;
  const parts: ReactNode[] = [];
  let cursor = 0;
  ranges.forEach(([start, end], i) => {
    if (start > cursor) parts.push(<Fragment key={`t${i}`}>{text.slice(cursor, start)}</Fragment>);
    parts.push(
      <mark key={`m${i}`} className="bg-transparent font-strong text-fg">
        {text.slice(start, end)}
      </mark>,
    );
    cursor = end;
  });
  if (cursor < text.length) parts.push(<Fragment key="tail">{text.slice(cursor)}</Fragment>);
  return <>{parts}</>;
}

/**
 * ⌘K command palette. Local commands are fuzzy-matched instantly; `sources` are queried in parallel after a
 * short debounce with an `AbortSignal` (a newer query cancels the older requests), and their results stream in
 * as each source answers. A failing source adds a quiet notice and never hides what the other sources found.
 * With an empty query the palette shows recents — including records found earlier, replayed through
 * `onNavigate`.
 */
export function CommandPalette({
  open,
  onOpenChange,
  commands,
  loadCommands,
  sources,
  onNavigate,
  placeholder: placeholderProp,
  emptyMessage: emptyMessageProp,
  messages: messagesProp,
  recentKey,
  maxRecent = 5,
  initialQuery = '',
  groupOrder,
  groupLabel,
  onCreate,
  createLabel,
  footer,
  onPeek,
  renderStatus,
  className,
}: CommandPaletteProps) {
  const id = useId();
  const t = useDsT();
  const placeholder = placeholderProp ?? t('ds.commandPalette.placeholder', 'Type a command or search…');
  const emptyMessage = emptyMessageProp ?? t('ds.commandPalette.empty', 'No matching commands');
  const listboxId = `${id}-listbox`;
  const createId = `${id}-create`;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [panelNode, setPanelNode] = useState<HTMLDivElement | null>(null);
  const [query, setQuery] = useState(initialQuery);
  const [active, setActive] = useState(0);
  const [sourceState, setSourceState] = useState<Record<string, SourceState>>({});
  const [recentIds, setRecentIds] = useState<string[]>(() => getRecent(recentKey));
  const messages = useMemo<CommandPaletteMessages>(
    () => ({
      ...defaultCommandPaletteMessages,
      dialogLabel: t('ds.commandPalette.dialogLabel', defaultCommandPaletteMessages.dialogLabel),
      listLabel: t('ds.commandPalette.listLabel', defaultCommandPaletteMessages.listLabel),
      searching: t('ds.common.searching', defaultCommandPaletteMessages.searching),
      recent: t('ds.commandPalette.recent', defaultCommandPaletteMessages.recent),
      navigate: t('ds.commandPalette.navigate', defaultCommandPaletteMessages.navigate),
      run: t('ds.commandPalette.run', defaultCommandPaletteMessages.run),
      close: t('ds.commandPalette.close', defaultCommandPaletteMessages.close),
      peek: t('ds.commandPalette.peek', defaultCommandPaletteMessages.peek),
      sourceFailed: (label) => t('ds.commandPalette.sourceFailed', '{label} could not be searched right now.', { label }),
      results: (count) => t('ds.commandPalette.results', count === 1 ? '1 result' : '{count} results', { count }),
      ...messagesProp,
    }),
    [messagesProp, t],
  );
  const headingFor = (group: string) => (groupLabel ? groupLabel(group) : BUILT_IN_GROUPS.has(group) ? t(`ds.commandPalette.groups.${group.toLowerCase()}`, group) : group);

  useFocusTrap(panelRef, open && panelNode != null, { initialFocus: false, restoreFocus: true });

  useEffect(() => {
    if (!open) {
      setPanelNode(null);
      return;
    }
    setQuery(initialQuery);
    setActive(0);
    setSourceState({});
    setRecentIds(getRecent(recentKey));
  }, [open, initialQuery, recentKey]);

  useEffect(() => {
    if (!open || !panelNode) return;
    queueMicrotask(() => inputRef.current?.focus({ preventScroll: true }));
  }, [open, panelNode]);

  const allSources = useMemo<CommandSource[]>(() => {
    const list = [...(sources ?? [])];
    if (loadCommands) list.push({ id: LEGACY_SOURCE_ID, label: '', minQueryLength: 1, search: (q, signal) => loadCommands(q, signal) });
    return list;
  }, [sources, loadCommands]);

  // Typed prefixes: `s:SHP-1` asks only the sources scoped `s` and matches only commands scoped `s`.
  const scopes = useMemo(() => new Set(allSources.map((s) => s.scope).filter((s): s is string => !!s)), [allSources]);
  const { scope, rest } = useMemo(() => parseScopedQuery(query, scopes), [query, scopes]);
  const scopeList = useMemo(() => [...scopes], [scopes]);

  useEffect(() => {
    if (!open) return;
    const q = rest.trim();
    const wanted = allSources.filter((s) => (scope ? s.scope === scope : true) && q.length >= (s.minQueryLength ?? 2));
    if (wanted.length === 0) {
      setSourceState({});
      return;
    }
    const controller = new AbortController();
    // Previous items stay visible (re-ranked against the new query) until the source answers: no flicker.
    setSourceState((prev) => Object.fromEntries(wanted.map((s) => [s.id, { status: 'loading', items: prev[s.id]?.items ?? [] }])));
    const timer = setTimeout(() => {
      for (const source of wanted) {
        let pending: Promise<Command[]>;
        try {
          pending = source.search(q, controller.signal);
        } catch (err) {
          pending = Promise.reject(err);
        }
        pending.then(
          (items) => {
            if (!controller.signal.aborted) setSourceState((prev) => ({ ...prev, [source.id]: { status: 'done', items } }));
          },
          () => {
            if (!controller.signal.aborted) setSourceState((prev) => ({ ...prev, [source.id]: { status: 'error', items: [] } }));
          },
        );
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, rest, scope, allSources]);

  const loading = Object.values(sourceState).some((s) => s.status === 'loading');
  const failedSources = allSources.filter((s) => s.label && sourceState[s.id]?.status === 'error');

  const groups = useMemo<CommandGroup[]>(() => {
    const q = rest.trim();
    const local = rankCommands(q, scope ? commands.filter((c) => c.scope === scope) : commands);
    const seen = new Set(local.map((r) => r.command.id));
    const remote: RankedCommand[] = [];
    for (const source of allSources) {
      if (scope && source.scope !== scope) continue;
      for (const item of sourceState[source.id]?.items ?? []) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        const match = matchCommand(q, item);
        const command = item.group || !source.label ? item : { ...item, group: source.label };
        remote.push({ command, score: Math.min(match?.score ?? REMOTE_BASE_SCORE, 99) + (source.rank ?? 0), ranges: match?.ranges ?? [] });
      }
    }
    const merged = [...local, ...remote];
    const grouped = order(groupCommands(q ? merged.slice().sort((a, b) => b.score - a.score) : merged), groupOrder ?? DEFAULT_COMMAND_GROUP_ORDER);
    if (q || scope) return grouped;
    const byId = new Map(commands.map((c) => [c.id, c]));
    const snaps = getRecentSnapshots(recentKey);
    const recent: Command[] = [];
    for (const rid of recentIds) {
      if (recent.length >= maxRecent) break;
      const known = byId.get(rid);
      if (known) {
        recent.push(known);
        continue;
      }
      const snap = snaps[rid];
      if (snap && onNavigate) {
        recent.push({ id: snap.id, label: snap.label, group: snap.group, description: snap.description, icon: snap.icon, href: snap.href, onRun: () => onNavigate(snap.href) });
      }
    }
    return recent.length
      ? [{ label: messages.recent, commands: recent, entries: recent.map((command) => ({ command, score: 1, ranges: [] })) }, ...grouped]
      : grouped;
    // `recentIds` changes whenever a snapshot is written, so reading the snapshot store here is safe.
  }, [rest, scope, commands, allSources, sourceState, recentIds, recentKey, maxRecent, groupOrder, onNavigate, messages.recent]);

  /**
   * Rows carry their absolute index. `ModalOverlay` can re-render on its own (presence), which
   * re-invokes this render prop, so the numbering must not come from a counter mutated during render.
   */
  const indexed = useMemo(() => {
    let next = 0;
    return groups.map((group) => ({ label: group.label, rows: group.entries.map((entry) => ({ entry, index: next++ })) }));
  }, [groups]);
  const flat = useMemo(() => indexed.flatMap((g) => g.rows.map((row) => ({ entry: row.entry, group: g.label }))), [indexed]);
  const trimmed = rest.trim();
  const showCreate = flat.length === 0 && !!onCreate && trimmed.length > 0 && !loading;
  const activeIndex = Math.min(active, Math.max(0, flat.length - 1));
  const activeId = showCreate ? createId : flat[activeIndex] ? `${id}-opt-${activeIndex}` : undefined;

  useEffect(() => {
    if (!activeId) return;
    document.getElementById(activeId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const run = useCallback(
    (command: Command) => {
      if (command.disabled) return;
      if (command.href) {
        pushRecentSnapshot(
          {
            id: command.id,
            label: command.label,
            href: command.href,
            group: command.group,
            description: command.description,
            icon: typeof command.icon === 'string' ? command.icon : undefined,
          },
          recentKey,
        );
      }
      setRecentIds(pushRecent(command.id, recentKey));
      close();
      void command.onRun();
    },
    [close, recentKey],
  );

  const create = useCallback(() => {
    if (!onCreate || !trimmed) return;
    close();
    onCreate(trimmed);
  }, [onCreate, trimmed, close]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case Keys.ArrowDown:
        e.preventDefault();
        setActive(flat.length ? (activeIndex + 1) % flat.length : 0);
        break;
      case Keys.ArrowUp:
        e.preventDefault();
        setActive(flat.length ? (activeIndex - 1 + flat.length) % flat.length : 0);
        break;
      case Keys.Home:
        if (e.ctrlKey || e.metaKey || query === '') {
          e.preventDefault();
          setActive(0);
        }
        break;
      case Keys.End:
        if (e.ctrlKey || e.metaKey || query === '') {
          e.preventDefault();
          setActive(Math.max(0, flat.length - 1));
        }
        break;
      case Keys.Enter: {
        e.preventDefault();
        if (showCreate) {
          create();
          break;
        }
        const entry = flat[activeIndex];
        if (entry) run(entry.entry.command);
        break;
      }
      case Keys.Escape:
        e.preventDefault();
        e.stopPropagation();
        close();
        break;
      case Keys.Tab: {
        if (!onPeek || e.shiftKey) break;
        const entry = flat[activeIndex];
        if (!entry || entry.entry.command.disabled) break;
        e.preventDefault();
        onPeek(entry.entry.command);
        break;
      }
    }
  };

  const attachPanel = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    if (node) setPanelNode(node);
  }, []);

  return (
    <ModalOverlay open={open} onDismiss={close} data-command-overlay="" className="zo-command-overlay items-start justify-center p-4 pt-[12vh]">
      {(state) => (
        <div
          ref={attachPanel}
          role="dialog"
          aria-modal="true"
          aria-label={messages.dialogLabel}
          data-state={state}
          className={cn(
            'zo-floating zo-command-palette flex max-h-[min(72vh,640px)] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-surface-3 text-fg shadow-4',
            className,
          )}
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-border-subtle px-3">
            <Icon name="search" size={16} className="shrink-0 text-fg-subtle" />
            <input
              ref={inputRef}
              role="combobox"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-activedescendant={activeId}
              aria-autocomplete="list"
              aria-label={placeholder}
              aria-busy={loading || undefined}
              autoComplete="off"
              spellCheck={false}
              placeholder={placeholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              className="h-control-lg min-w-0 flex-1 bg-transparent text-md text-fg outline-none placeholder:text-fg-subtle"
            />
            {loading ? <Spinner size={14} label={messages.searching} /> : <Kbd size="sm">Esc</Kbd>}
          </div>
          <div role="listbox" id={listboxId} aria-label={messages.listLabel} className="min-h-0 flex-1 overflow-y-auto p-1 scrollbar-thin">
            {flat.length === 0 ? (
              showCreate ? (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <div
                  id={createId}
                  role="option"
                  tabIndex={-1}
                  aria-selected="true"
                  onClick={create}
                  className="flex h-control cursor-pointer items-center gap-2 rounded-sm bg-selected px-2 type-body"
                >
                  <span className="flex size-4 shrink-0 items-center justify-center text-accent">
                    <Icon name="plus" size={14} />
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {typeof createLabel === 'function' ? createLabel(trimmed) : (createLabel ?? t('ds.commandPalette.create', 'Create “{query}”', { query: trimmed }))}
                  </span>
                  <Kbd keys="enter" size="sm" />
                </div>
              ) : (
                <div className="px-3 py-8 text-center type-body text-fg-muted">{loading ? messages.searching : emptyMessage}</div>
              )
            ) : (
              indexed.map((group) => (
                <div key={group.label} role="group" aria-labelledby={`${id}-g-${group.label}`} className="pb-1">
                  <div id={`${id}-g-${group.label}`} className={groupHeading}>
                    {headingFor(group.label)}
                  </div>
                  {group.rows.map(({ entry: { command, ranges }, index }) => {
                    const selected = index === activeIndex;
                    return (
                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                      <div
                        key={`${group.label}:${command.id}`}
                        id={`${id}-opt-${index}`}
                        role="option"
                        tabIndex={-1}
                        aria-selected={selected}
                        aria-disabled={command.disabled || undefined}
                        data-selected={selected || undefined}
                        onMouseMove={() => setActive(index)}
                        onClick={() => run(command)}
                        className={cn(
                          'relative flex min-h-control cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1 type-body transition-colors motion-fast',
                          selected && 'bg-selected text-fg',
                          command.disabled && 'cursor-not-allowed opacity-40',
                        )}
                      >
                        {selected ? <span aria-hidden className="absolute inset-y-1.5 start-0 w-0.5 rounded-full bg-accent" /> : null}
                        <span className={cn('flex size-4 shrink-0 items-center justify-center', selected ? 'text-fg' : 'text-fg-subtle')}>{renderIcon(command.icon)}</span>
                        <span className="flex min-w-0 flex-1 flex-col leading-tight">
                          <span className="truncate">
                            <Highlight text={command.label} ranges={ranges} />
                          </span>
                          {command.description ? <span className="truncate type-caption text-fg-subtle">{command.description}</span> : null}
                        </span>
                        {command.status && renderStatus ? (
                          <span data-slot="status" className="shrink-0">
                            {renderStatus(command)}
                          </span>
                        ) : null}
                        {command.group && group.label !== command.group ? (
                          <span aria-hidden="true" className="shrink-0 type-caption text-fg-subtle">
                            {command.group}
                          </span>
                        ) : null}
                        {command.shortcut ? <Kbd keys={command.shortcut} size="sm" /> : null}
                        {selected && !command.shortcut ? <Kbd keys="enter" size="sm" className="shrink-0" /> : null}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
          {failedSources.length ? (
            <ul className="m-0 flex shrink-0 list-none flex-col gap-0.5 border-t border-border-subtle px-3 py-1.5 type-caption text-fg-muted" aria-live="polite">
              {failedSources.map((s) => (
                <li key={s.id} className="flex items-center gap-1.5">
                  <Icon name="alert" size={12} className="shrink-0 text-warning" />
                  {messages.sourceFailed(s.label)}
                </li>
              ))}
            </ul>
          ) : null}
          <span className="sr-only" aria-live="polite">
            {trimmed && !loading ? messages.results(flat.length) : ''}
          </span>
          <div className="flex shrink-0 items-center gap-3 border-t border-border-subtle px-3 py-1.5 type-caption text-fg-subtle">
            {footer ?? (
              <>
                <span className="flex items-center gap-1">
                  <Kbd keys="up" size="sm" />
                  <Kbd keys="down" size="sm" /> {messages.navigate}
                </span>
                <span className="flex items-center gap-1">
                  <Kbd keys="enter" size="sm" /> {messages.run}
                </span>
                {onPeek ? (
                  <span className="flex items-center gap-1">
                    <Kbd keys="tab" size="sm" /> {messages.peek}
                  </span>
                ) : null}
                <span className="flex items-center gap-1">
                  <Kbd keys="esc" size="sm" /> {messages.close}
                </span>
                {scopeList.length ? (
                  <span className="ms-auto hidden truncate font-mono sm:inline" title={messages.prefixes(scopeList)}>
                    {messages.prefixes(scopeList)}
                  </span>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </ModalOverlay>
  );
}

/** Applies a preferred heading order, keeping unlisted groups in their first-seen order. */
function order(groups: CommandGroup[], preferred?: string[]): CommandGroup[] {
  if (!preferred?.length) return groups;
  const rank = new Map(preferred.map((label, i) => [label, i]));
  return groups
    .map((group, i) => ({ group, i }))
    .sort((a, b) => (rank.get(a.group.label) ?? preferred.length + a.i) - (rank.get(b.group.label) ?? preferred.length + b.i))
    .map((entry) => entry.group);
}
